/**
 * Настройки шкалы цены.
 *
 * Тот же список, что человек привык видеть в чужих терминалах, — но
 * только те пункты, за которыми стоит настоящее поведение графика.
 * Галочка, которая ничего не меняет, хуже её отсутствия: человек решит,
 * что сломан график, а сломано обещание.
 *
 * Чего здесь нет и почему — сказано в конце файла. Список неполон
 * намеренно, и это лучше, чем полный список наполовину рабочих
 * переключателей.
 */

/** Как шкала показывает цену. */
export type ScaleMode = 'normal' | 'percent' | 'indexed' | 'log';

export interface ScaleSettings {
  readonly mode: ScaleMode;
  /** Ось растёт вниз: так смотрят на инструменты, которые шортят. */
  readonly invert: boolean;
  /** Шкала слева, а не справа. */
  readonly left: boolean;
  /**
   * Не подгонять масштаб под индикаторы.
   *
   * Индикатор со своим размахом — объём, открытый интерес — растягивает
   * шкалу так, что свечи сжимаются в полоску. Здесь он перестаёт влиять
   * на автоподбор, оставаясь на графике.
   */
  readonly ignoreIndicators: boolean;

  /* --- метки на шкале */
  readonly lastValue: boolean;
  readonly indicatorTitles: boolean;
  readonly indicatorValues: boolean;

  /* --- линии на полотне */
  readonly priceLine: boolean;
  /** Максимум и минимум суток — линией и меткой на шкале. */
  readonly highLow: boolean;
  /**
   * Сколько осталось до закрытия бара.
   *
   * Считается по таймфрейму и часам, а не приходит от биржи: граница
   * бара — арифметика, и спрашивать её не у кого.
   */
  readonly countdown: boolean;
}

export const DEFAULT_SCALE: ScaleSettings = {
  mode: 'normal',
  invert: false,
  left: false,
  ignoreIndicators: false,
  lastValue: true,
  indicatorTitles: false,
  indicatorValues: true,
  priceLine: true,
  highLow: false,
  countdown: true,
};

/** Режим шкалы числом, как его понимает библиотека графика. */
export function modeCode(mode: ScaleMode): 0 | 1 | 2 | 3 {
  switch (mode) {
    case 'log':
      return 1;
    case 'percent':
      return 2;
    case 'indexed':
      return 3;
    default:
      return 0;
  }
}

/**
 * Разбор сохранённого.
 *
 * Профиль живёт дольше кода: запись прошлой версии не должна ни ронять
 * график, ни молча включать то, чего человек не выбирал. Всё, чего в
 * записи нет или что записано не тем типом, берётся из умолчаний.
 */
export function parseScale(raw: unknown): ScaleSettings {
  if (typeof raw !== 'object' || raw === null) return DEFAULT_SCALE;

  const value = raw as Partial<Record<keyof ScaleSettings, unknown>>;
  const bool = (key: keyof ScaleSettings): boolean =>
    typeof value[key] === 'boolean' ? (value[key] as boolean) : DEFAULT_SCALE[key] === true;

  const mode = value.mode;
  return {
    mode:
      mode === 'log' || mode === 'percent' || mode === 'indexed' || mode === 'normal'
        ? mode
        : DEFAULT_SCALE.mode,
    invert: bool('invert'),
    left: bool('left'),
    ignoreIndicators: bool('ignoreIndicators'),
    lastValue: bool('lastValue'),
    indicatorTitles: bool('indicatorTitles'),
    indicatorValues: bool('indicatorValues'),
    priceLine: bool('priceLine'),
    highLow: bool('highLow'),
    countdown: bool('countdown'),
  };
}

/*
 * Чего в списке нет.
 *
 * «Метка имени инструмента» и «Не перекрывать метки» есть в чужих
 * терминалах, но их рисует не библиотека графика, а сам терминал поверх
 * канвы. Сделать их можно — это отдельная работа со своим слоем
 * отрисовки, а не галочка. Пока их нет, чтобы список не обещал того,
 * чего не делает.
 *
 * Обратный отсчёт из этого списка уже вышел: он оказался не слоем
 * отрисовки, а одной подписью поверх канвы — время закрытия бара
 * считается из таймфрейма, а место для неё даёт `priceToCoordinate`.
 *
 * «Метки макс. и мин. цен» отдельным пунктом тоже нет: у нас это одно и
 * то же с линиями — линия суточного максимума приходит вместе со своей
 * меткой на шкале, разделять их значило бы городить два переключателя
 * вокруг одной черты.
 */
